import { createRoot } from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import App from "./App.tsx";
import "./index.css";
import { installGlobalErrorReporting } from "./lib/errorReporter";
installGlobalErrorReporting();

const BUILD_VERSION = import.meta.env.VITE_BUILD_TIMESTAMP || Date.now().toString();
const CACHE_VERSION_KEY = "roots-cache-version";
const rootElement = document.getElementById("root");

const clearAllCaches = async () => {
  if (!("caches" in window)) return;

  const cacheNames = await window.caches.keys();
  await Promise.all(cacheNames.map((cacheName) => window.caches.delete(cacheName)));
};

const unregisterAllServiceWorkers = async () => {
  if (!("serviceWorker" in navigator)) return;
  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map((registration) => registration.unregister().catch(() => {})));
  } catch {
    // Ignore service worker unregister errors
  }
};

const renderApp = () => {
  if (!rootElement) return;

  createRoot(rootElement).render(<HelmetProvider><App /></HelmetProvider>);
  document.getElementById("ssr-skeleton")?.remove();
};

const isInIframe = (() => {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
})();

const isPreviewHost =
  window.location.hostname.includes("id-preview--") ||
  window.location.hostname.includes("lovableproject.com");

const bootstrap = async () => {
  const storedVersion = localStorage.getItem(CACHE_VERSION_KEY);
  const needsCleanup = storedVersion !== BUILD_VERSION;

  if (isPreviewHost || isInIframe) {
    await Promise.all([unregisterAllServiceWorkers(), clearAllCaches()]);
    renderApp();
    return;
  }

  if (needsCleanup) {
    await Promise.all([unregisterAllServiceWorkers(), clearAllCaches()]);
    localStorage.setItem(CACHE_VERSION_KEY, BUILD_VERSION);
    window.location.reload();
    return;
  }

  localStorage.setItem(CACHE_VERSION_KEY, BUILD_VERSION);
  renderApp();
};

bootstrap().catch(() => {
  renderApp();
});